import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import classNames from 'classnames';

import useApi from 'common/useApi';
import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';
import useClickOutside from 'common/useClickOutside';

import './FlagForRepair.scss';

type RepairLocation = { id: number, name: string };

const PARTS = [
  { value: 'h', name: 'Hashboard' },
  { value: 'p', name: 'Power Supply' },
  { value: 'u', name: 'Issue Unidentified' },
  { value: 'm', name: 'Multiple Issues' },
];

// Inline "flag for repair" control for the Workers table action bar: opens a
// repair ticket for the currently selected workers (mirrors the EditWorkers
// popover pattern so it can read the table's `checkedIds`).
const FlagForRepair = ({
  checkedIds,
  setCheckedIds,
}: {
  checkedIds: Array<number>,
  setCheckedIds: (ids: Array<number>) => void,
}) => {
  const { addToast } = useContext(UiContext);
  const { user } = useContext(AuthContext)!;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));

  const { data: locations } = useApi('get', '/api/repair-locations');
  const { isSuccess, isError, isLoading, mutate } = useApi('post', '/api/workers/flag-for-repair');

  const formMethods = useForm({ mode: 'onChange' });
  const { handleSubmit, register, reset } = formMethods;

  const toggle = () => {
    if (checkedIds.length === 0) {
      addToast!('No worker selected', 'Error');
      return;
    }
    if (!locations || locations.length === 0) {
      addToast!('No repair locations configured — contact an admin', 'Error');
      return;
    }
    if (!open) {
      reset({
        location: locations[0]?.id,
        parts_needing_repair: 'h',
        parts_in_repair: '',
        contact_name: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
        contact_email: user?.email ?? '',
        contact_phone: '',
      });
    }
    setOpen((o) => !o);
  };

  const onSubmit = (form: Record<string, string>) => {
    mutate({ worker_ids: checkedIds, ...form });
  };

  useEffect(() => {
    if (isSuccess) {
      addToast!(`Flagged ${checkedIds.length} worker(s) for repair`);
      setOpen(false);
      setCheckedIds([]);
      navigate('/repair');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) addToast!('Could not flag workers for repair', 'Error');
  }, [isError]);

  return (
    <div ref={ref} className='flag-for-repair'>
      <button type='button' className='primary small' onClick={toggle}>
        Flag for repair
      </button>
      <div className={classNames({ 'flag-panel': true, open })}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <label>
              Location
              <select {...register('location', { required: true })}>
                {(locations ?? []).map((loc: RepairLocation) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </label>
            <label>
              What needs repair
              <select {...register('parts_needing_repair', { required: true })}>
                {PARTS.map((p) => (
                  <option key={p.value} value={p.value}>{p.name}</option>
                ))}
              </select>
            </label>
            <label>
              Parts in repair
              <input type='text' placeholder='e.g. hashboard 2' {...register('parts_in_repair')} />
            </label>
            <label>
              Contact name
              <input type='text' {...register('contact_name', { required: true })} />
            </label>
            <label>
              Contact email
              <input type='text' {...register('contact_email')} />
            </label>
            <button type='submit' className='primary small' disabled={isLoading}>
              Flag {checkedIds.length} worker(s)
            </button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default FlagForRepair;
